import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4s05a91e6in3mfvk90g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-block mb-4 px-4 py-1.5 bg-cyan-100 rounded-full">
            <span className="text-cyan-600 text-xs font-semibold uppercase tracking-wider">Get In Touch</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Contact Us
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Have questions about our VPN service? We're here to help. Reach out to our team and we'll get back to you as soon as possible.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="bg-white rounded-3xl p-10 shadow-lg mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-8">Send us a message</h3>
              <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
                      placeholder="+1 234 567 8900"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
                      placeholder="How can we help?"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none text-sm"
                      placeholder="Tell us more about your inquiry..."
                    />
                    <p className="text-xs text-gray-500 mt-2">{formData.message.length}/500 characters</p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-4 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white font-bold rounded-xl hover:from-cyan-600 hover:to-cyan-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                      <p className="text-green-700 text-sm font-medium">Thank you! Your message has been sent successfully.</p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                      <p className="text-red-700 text-sm font-medium">Something went wrong. Please try again.</p>
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>

          <div className="space-y-8">
            <div className="bg-white rounded-3xl p-10 shadow-lg">
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-cyan-500 to-purple-600 rounded-xl flex-shrink-0">
                  <i className="ri-mail-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">Email Us</h4>
                  <p className="text-gray-600 mb-2">Send us an email anytime</p>
                  <a href="mailto:information@zaleteljdoo.com" className="text-cyan-600 font-semibold hover:text-cyan-700">
                    information@zaleteljdoo.com
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-10 shadow-lg">
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-cyan-500 to-purple-600 rounded-xl flex-shrink-0">
                  <i className="ri-phone-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">Call Us</h4>
                  <p className="text-gray-600 mb-2">Available during business hours</p>
                  <a href="tel:+18554195724" className="text-cyan-600 font-semibold hover:text-cyan-700">
                    +1 855 419 5724
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-10 shadow-lg">
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-cyan-500 to-purple-600 rounded-xl flex-shrink-0">
                  <i className="ri-map-pin-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">Visit Us</h4>
                  <p className="text-gray-600 mb-2">Our office location</p>
                  <p className="text-gray-700 font-medium">
                    Jama 16<br />
                    1234 Menges<br />
                    Slovenia
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-cyan-500 to-purple-600 rounded-3xl p-10 text-white shadow-lg">
              <h4 className="text-2xl font-bold mb-4">Need Immediate Help?</h4>
              <p className="mb-6 text-white/90">
                Our support team is available 24/7 to assist you with any questions or technical issues.
              </p>
              <button
                onClick={() => {
                  const element = document.getElementById('support');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-6 py-3 bg-white text-cyan-600 font-bold rounded-xl hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
              >
                Visit Support Center
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
